import React, { useEffect, useState, useCallback } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Plus, Trash } from "@phosphor-icons/react";

export default function AdminCategories() {
  const [cats, setCats] = useState([]);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/categories");
    setCats(data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/categories", { name });
      setName("");
      toast.success("Category added");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this category?")) return;
    try {
      await api.delete(`/categories/${id}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <DashboardLayout>
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Admin</div>
        <h1 className="font-heading text-2xl font-semibold text-zinc-900">Expense categories</h1>
        <p className="text-sm text-zinc-500 mt-1">
          Customize the taxonomy used across all submissions.
        </p>
      </div>

      <form onSubmit={add} className="mt-6 flex items-center gap-2 max-w-md">
        <Input
          data-testid="new-category-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Client Entertainment"
          required
        />
        <Button
          type="submit"
          disabled={saving || !name.trim()}
          data-testid="add-category-btn"
          className="bg-blue-600 hover:bg-blue-700 text-white"
        >
          <Plus size={16} className="mr-1.5" /> Add
        </Button>
      </form>

      <div className="mt-6 bg-white border border-zinc-200 rounded-lg overflow-hidden max-w-2xl">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-[0.14em] text-zinc-500 border-b border-zinc-200">
              <th className="px-4 py-2.5 font-medium">Category</th>
              <th className="px-4 py-2.5 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {cats.map((c) => (
              <tr key={c.id} data-testid={`cat-row-${c.id}`} className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50">
                <td className="px-4 py-3 text-zinc-900">{c.name}</td>
                <td className="px-4 py-3 text-right">
                  <Button
                    variant="ghost"
                    size="sm"
                    data-testid={`delete-cat-${c.id}`}
                    onClick={() => remove(c.id)}
                    className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                  >
                    <Trash size={14} />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </DashboardLayout>
  );
}
